/**
 * @file react 组件生命周期相关 hooks
 */
import { useUnmount as useUnmountOrigin } from 'ahooks';
import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * 只在组件挂载 (首次渲染) 时执行该 effect, 组件后续的刷新不会再触发该 effect.
 * 如果该 effect 返回了一个清理函数, 那么它也只会在组件销毁时执行.
 *
 * @category React Component Lifecycle Hook
 * @param effect - 要执行的 effect, 类型同 useEffect 的参数
 *
 * @example
 * ```javascript
 * useOnMount(() => {
 *   addEventListener(handler)
 *   return () => removeEventListener(handler)
 * })
 * ```
 */
export const useOnMount = (effect) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(effect, []);
};
/**
 * 组件解绑时嗲用
 * 文档：https://ahooks.js.org/zh-CN/hooks/use-unmount
 *
 * @category React Component Lifecycle Hook
 */
export const useUnmount = useUnmountOrigin;
/**
 * 组件首次渲染时不执行 effect, 只在后续 deps 变化时执行.
 *
 * @category React Component Lifecycle Hook
 * @param effect - 要执行的 effect, 类型同 useEffect 的参数
 * @param deps - 同 useEffect 的 deps
 */
export const useOnUpdate = (effect, deps) => {
    const isMounted = useRef(false);
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return undefined;
        }
        return effect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};
/**
 * 返回一个强制更新函数, 通常用于作为 useEffect 的依赖项, 然后通过执行自己来强制触发 useEffect 更新.
 * 由于该函数通常不在 useEffect 的函数体内出现, 会被 eslint 上报为多余的依赖项,
 * 往往需要添加 `// eslint-disable-next-line react-hooks/exhaustive-deps` 来阻止 eslint 报错.
 *
 * @category React Component Lifecycle Hook
 * @returns 返回 [上述函数, 内部 token 值 (从 0 开始, 每执行一次 +1)]
 *
 * @example
 * ```javascript
 * const [update] = useForceUpdate()
 *
 * React.useEffect(() =>
 *   api.queryLoad()
 * }, [update])
 *
 * <button onClick={() => update()}>Click</button>
 * ```
 */
export const useForceUpdate = () => {
    const [token, setToken] = useState(0);
    const update = useCallback(() => {
        setToken(prevToken => prevToken + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);
    // 将 token 也返回, 少数场景下可以通过判断 token === 0 来避免在初始化时就触发特定逻辑
    return [update, token];
};
/**
 * 返回一个标志位，当依赖变化时，重新构造组件。
 * 通常用于底层数据变化时（比如切换了用户），直接重新构造底层组件，以达到重新请求接口数据和渲染的效果。
 * 这种方法，比让底层组件的子组件 Hook 逐个观测底层数据的变化而言，更为简单和易维护。
 *
 * @category React Component Lifecycle Hook
 * @param dep - 依赖
 * @returns 渲染标志
 */
export const useRecreateOn = (dep) => {
    const [renderSign, setRenderSign] = useState(false);
    useEffect(() => {
        setRenderSign(v => (v ? false : v));
        requestAnimationFrame(() => {
            setRenderSign(true);
        });
    }, [dep]);
    return renderSign;
};
//# sourceMappingURL=data:application/json;base64,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